/* wnf-fabric-gallery-2026-10-03: how the photo follows a fabric change on wnfb PDPs.
   snippets/wnf-fabric-gallery-core.liquid groups the photos per fabric and patches Motion's
   theme.Product while the page parses (Motion starts its sections before deferred scripts run).
   This file is the visual layer those patches call once it has loaded:
   - a fabric change replaces the photo in place with a 180 ms crossfade (instant with Reduce Motion);
   - each fabric's lead photo is fetched once the page is idle, so a change never shows a blank frame;
   - desktop: resting the pointer on a swatch previews that fabric;
   - phones: a small live photo of the chosen fabric sits beside the colour label;
   - "Compare all N colors" shows every fabric's lead photo side by side. */
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const FADE = 180;

  function ready(img, ms) {
    return new Promise(resolve => {
      let done = false, timer;
      const finish = () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        const decoded = img.decode ? img.decode().catch(() => {}) : Promise.resolve();
        decoded.then(resolve);
      };
      if (img.complete && img.naturalWidth) return finish();
      if (img.loading === 'lazy') img.loading = 'eager';
      img.addEventListener('load', finish, { once: true });
      img.addEventListener('error', finish, { once: true });
      timer = setTimeout(finish, ms);
    });
  }

  function plural(word) { return /(s|sh|ch|x|z)$/.test(word) ? word + 'es' : word + 's'; }

  class Gallery {
    constructor(product) {
      this.p = product;
      this.photos = product.cache.mainSlider;
      this.main = this.photos.closest('.product__main-photos') || this.photos.parentNode;
      this.core = this.photos._wnfFabric || null;
      this.preview = null;
      this.extras = false;
    }

    get scoped() { return !!(this.core && this.core.scoped); }

    value(variant) {
      variant = variant || (this.p.variants && this.p.variants.currentVariant);
      return variant && this.core ? variant['option' + (this.core.option + 1)] : null;
    }

    slideImg(id) {
      const slide = this.photos.querySelector('.product-main-slide[data-media-id="' + id + '"]');
      return slide && slide.querySelector('.image-wrap:not(.hide) img');
    }

    selectedImg() {
      return this.photos.querySelector('.flickity-viewport .is-selected .image-wrap:not(.hide) img');
    }

    frame(img) { if (typeof this.photos._wnfFrame === 'function') this.photos._wnfFrame(img); }

    // An image laid exactly over `like` (the photo on screen), above the slider.
    overlay(like, src, srcset, sizes) {
      const a = like.getBoundingClientRect(), b = this.main.getBoundingClientRect();
      const cs = getComputedStyle(like);
      const holder = like.closest('.image-wrap') || like.parentNode;
      let bg = getComputedStyle(holder).backgroundColor;
      if (!bg || bg === 'transparent' || bg === 'rgba(0, 0, 0, 0)') bg = getComputedStyle(document.body).backgroundColor;
      const img = document.createElement('img');
      img.className = 'wnf-fg-overlay';
      img.alt = '';
      img.setAttribute('aria-hidden', 'true');
      img.decoding = 'async';
      Object.assign(img.style, {
        left: a.left - b.left + 'px', top: a.top - b.top + 'px', width: a.width + 'px', height: a.height + 'px',
        objectFit: cs.objectFit, objectPosition: cs.objectPosition, backgroundColor: bg
      });
      if (srcset) { img.sizes = sizes || a.width + 'px'; img.srcset = srcset; }
      img.src = src;
      this.main.appendChild(img);
      return img;
    }

    fadeOut(el) {
      if (reduce.matches) { el.remove(); return; }
      el.style.transition = 'opacity ' + FADE + 'ms ease-out';
      el.getBoundingClientRect();
      el.style.opacity = '0';
      setTimeout(() => el.remove(), FADE + 60);
    }

    // Freeze what is on screen before Motion swaps the photo.
    cover() {
      this.main.querySelectorAll('.wnf-fg-overlay.is-cover').forEach(el => el.remove());
      const pv = this.preview;
      this.preview = null;
      if (pv && pv.shown) {
        pv.el.style.transition = 'none';
        pv.el.style.opacity = '1';
        pv.el.classList.add('is-cover');
        return pv.el;
      }
      if (pv) pv.el.remove();
      const img = this.selectedImg();
      if (!img || !img.complete || !img.naturalWidth) return null;
      const el = this.overlay(img, img.currentSrc || img.src);
      el.classList.add('is-cover');
      return el;
    }

    // Fade the frozen photo away once the new one is decoded and framed.
    uncover(el) {
      if (!el) return;
      this.photos.dispatchEvent(new CustomEvent('wnf:reframe'));
      const img = this.selectedImg();
      if (!img) { this.fadeOut(el); return; }
      ready(img, 2500).then(() => { this.frame(img); this.fadeOut(el); });
    }

    showPreview(value) {
      const id = this.core.featured[value];
      const src = id && this.slideImg(id);
      const cur = this.selectedImg();
      if (!src || !cur) return;
      if (this.preview && this.preview.value === value) return;
      this.hidePreview(true);
      const el = this.overlay(cur, src.currentSrc || src.getAttribute('src'), src.getAttribute('srcset'), src.getAttribute('sizes'));
      el.style.opacity = '0';
      const pv = this.preview = { value, el, shown: false };
      ready(el, 1500).then(() => {
        if (this.preview !== pv) return;
        this.frame(el);
        pv.shown = true;
        el.style.transition = reduce.matches ? 'none' : 'opacity ' + FADE + 'ms ease-out';
        el.getBoundingClientRect();
        el.style.opacity = '1';
      });
    }

    hidePreview(now) {
      const pv = this.preview;
      this.preview = null;
      if (!pv) return;
      if (now || !pv.shown) pv.el.remove();
      else this.fadeOut(pv.el);
    }

    labelPreview(value) {
      if (!this.wrapper) return;
      const label = this.wrapper.querySelector('[data-variant-color-label]');
      this.wrapper.classList.toggle('wnf-fg-previewing', !!value);
      if (label) label.textContent = value || this.value() || label.textContent;
    }

    hover(value) {
      if (!value || value === this.value()) { this.hidePreview(); this.labelPreview(null); return; }
      this.showPreview(value);
      this.labelPreview(value);
    }

    bindHover() {
      if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      let enterTimer, leaveTimer;
      this.fieldset.querySelectorAll('.variant-input[data-value]').forEach(el => {
        el.addEventListener('pointerenter', e => {
          if (e.pointerType !== 'mouse') return;
          clearTimeout(leaveTimer);
          clearTimeout(enterTimer);
          enterTimer = setTimeout(() => this.hover(el.dataset.value), 60);
        });
      });
      this.fieldset.addEventListener('pointerleave', () => {
        clearTimeout(enterTimer);
        leaveTimer = setTimeout(() => this.hover(null), 120);
      });
      this.fieldset.addEventListener('click', () => { clearTimeout(enterTimer); clearTimeout(leaveTimer); }, true);
    }

    // Phones: the chosen fabric's photo beside the colour label, so a tap shows a result.
    buildPeek() {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'wnf-fg-peek';
      btn.innerHTML = '<span class="wnf-fg-peek__img"></span><span class="wnf-fg-peek__txt">See photos</span>';
      btn.addEventListener('click', () => {
        this.main.scrollIntoView({ behavior: reduce.matches ? 'auto' : 'smooth', block: 'start' });
      });
      const label = this.wrapper.querySelector('.variant__label');
      if (!label) return;
      label.after(btn);
      this.wrapper.classList.add('wnf-fg-has-peek');
      this.peek = btn;
      this.updatePeek();
    }

    updatePeek(variant) {
      if (!this.peek) return;
      const value = this.value(variant);
      const id = value && this.core.featured[value];
      const src = id && this.slideImg(id);
      this.peek.hidden = !src;
      if (!src) return;
      this.peek.setAttribute('aria-label', 'See ' + value + ' in the photos above');
      const box = this.peek.querySelector('.wnf-fg-peek__img');
      const img = document.createElement('img');
      img.alt = '';
      img.decoding = 'async';
      img.sizes = '80px';
      if (src.getAttribute('srcset')) img.srcset = src.getAttribute('srcset');
      img.src = src.currentSrc || src.getAttribute('src');
      img.style.opacity = '0';
      box.appendChild(img);
      ready(img, 2500).then(() => {
        if (img.parentNode !== box) return;
        img.style.transition = reduce.matches ? 'none' : 'opacity ' + FADE + 'ms ease-out';
        img.getBoundingClientRect();
        img.style.opacity = '1';
        [...box.children].forEach(old => { if (old !== img) setTimeout(() => old.remove(), FADE + 60); });
      });
    }

    // Every fabric's lead photo side by side; choosing one selects it.
    buildCompare() {
      const vals = this.core.values.filter(v => this.core.featured[v]);
      if (vals.length < 4) return;
      const noun = plural((this.fieldset.getAttribute('name') || 'color').toLowerCase());
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'wnf-fg-compare';
      btn.textContent = 'Compare all ' + vals.length + ' ' + noun;
      btn.addEventListener('click', () => this.openCompare(vals, noun));
      this.fieldset.after(btn);
      this.wrapper.classList.add('wnf-fg-has-compare');
    }

    openCompare(vals, noun) {
      let dlg = this.dialog;
      if (!dlg) {
        dlg = this.dialog = document.createElement('dialog');
        dlg.className = 'wnf-fg-sheet';
        const titleId = 'WnfFgSheet-' + this.p.sectionId;
        dlg.setAttribute('aria-labelledby', titleId);
        dlg.innerHTML = '<div class="wnf-fg-sheet__head"><h2 class="wnf-fg-sheet__title" id="' + titleId + '"></h2>' +
          '<button type="button" class="wnf-fg-sheet__close">Close</button></div><div class="wnf-fg-sheet__grid"></div>';
        dlg.querySelector('.wnf-fg-sheet__close').addEventListener('click', () => dlg.close());
        dlg.addEventListener('click', e => { if (e.target === dlg) dlg.close(); });
        document.body.appendChild(dlg);
      }
      dlg.querySelector('.wnf-fg-sheet__title').textContent = 'All ' + vals.length + ' ' + noun;
      const grid = dlg.querySelector('.wnf-fg-sheet__grid');
      grid.textContent = '';
      const current = this.value();
      vals.forEach(v => {
        const src = this.slideImg(this.core.featured[v]);
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'wnf-fg-sheet__item';
        item.setAttribute('aria-pressed', String(v === current));
        const img = document.createElement('img');
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        if (src) {
          img.sizes = '(max-width: 768px) 45vw, 220px';
          if (src.getAttribute('srcset')) img.srcset = src.getAttribute('srcset');
          img.src = src.currentSrc || src.getAttribute('src');
        }
        const label = document.createElement('span');
        label.textContent = v;
        item.append(img, label);
        item.addEventListener('click', () => {
          dlg.close();
          const input = [...this.fieldset.querySelectorAll('input[type="radio"]')].find(i => i.value === v);
          if (input && !input.checked) input.click();
        });
        grid.appendChild(item);
      });
      if (typeof dlg.showModal === 'function') dlg.showModal();
      else dlg.setAttribute('open', '');
    }

    preload() {
      const c = navigator.connection;
      if (c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || ''))) return;
      let ids;
      if (this.core) ids = this.core.values.map(v => this.core.featured[v]).filter(Boolean);
      else ids = (this.p.variantsObject || []).map(v => v.featured_media && v.featured_media.id).filter(Boolean);
      [...new Set(ids.map(String))].forEach(id => {
        const img = this.slideImg(id);
        if (img) ready(img, 15000);
      });
    }

    setup() {
      if (this.extras) return;
      this.extras = true;
      const idle = () => (window.requestIdleCallback ? requestIdleCallback(() => this.preload(), { timeout: 4000 }) : setTimeout(() => this.preload(), 1500));
      if (document.readyState === 'complete') idle();
      else window.addEventListener('load', idle, { once: true });
      if (!this.scoped) return;
      this.fieldset = this.p.container.querySelector('.variant-input-wrap[data-handle="' + this.core.handle + '"]');
      this.wrapper = this.fieldset && this.fieldset.closest('.variant-wrapper');
      if (!this.fieldset || !this.wrapper) return;
      this.bindHover();
      this.buildPeek();
      this.buildCompare();
    }

    onVariant(variant) {
      this.labelPreview(null);
      const label = this.wrapper && this.wrapper.querySelector('[data-variant-color-label]');
      if (label && this.value(variant)) label.textContent = this.value(variant);
      this.updatePeek(variant);
    }

    // Motion binds new listeners to the thumbnail links every time it builds a slider and never
    // removes the old ones; fresh nodes keep a rebuilt slider from driving the destroyed one.
    freshThumbLinks() {
      const thumbs = this.p.cache.thumbSlider;
      if (thumbs) thumbs.querySelectorAll('.product__thumb-item a, .product__thumb-arrow').forEach(el => el.replaceWith(el.cloneNode(true)));
    }
  }

  const galleries = new WeakMap();
  window.WNFFabricGallery = {
    get(product) {
      if (!galleries.has(product)) galleries.set(product, new Gallery(product));
      const g = galleries.get(product);
      product.container._wnfFg = g;
      return g;
    }
  };
  // Products Motion built before this file loaded.
  (window.__wnfFgProducts || []).forEach(p => window.WNFFabricGallery.get(p).setup());

  document.addEventListener('variantChange', event => {
    const g = event.target && event.target._wnfFg;
    if (g && g.scoped && event.detail && event.detail.variant) g.onVariant(event.detail.variant);
  }, true);
})();
